import { useEffect, useId, useRef, useState } from "react";

/**
 * The confirmation a family answers before a write lands — **and the panel's most constrained
 * component** (ADR 0021 decision 8, issue #302).
 *
 * ## It renders the server's words and composes none of its own
 *
 * This is the named control the ADR says it will be revised over, so it is worth stating
 * plainly rather than leaving to the test: **every sentence in this dialog arrives from the
 * server.** The question is the tool's own `message`. The lines beneath it are the tool's own
 * `doesNotReach`. The panel's entire vocabulary here is two button labels and a failure line,
 * and none of them says anything about *what is being written*.
 *
 * The reason is ADR 0014 decision 1. A confirmation proves the family's authenticated session
 * asked for one exact stored write — not that a person was shown the server's message rather
 * than a paraphrase of it. In a chat host that gap is unavoidable, because the host owns the
 * conversation. **Here we own the surface, so the gap is ours to close and closing it is free.**
 * A panel that summarised the write in its own words would re-create, on the one surface that
 * did not have to have it, exactly the weakness that decision records.
 *
 * It follows that a disclosure the family needs has to reach them *through* the server's
 * strings. That is already true and is why this works: `forget_this` builds the #241
 * *"N other things you asked me to remember had the very same words"* clause into its
 * `message`, so rendering the message verbatim discloses the count — the panel does not have
 * to assemble that sentence from `deleting.quotedOnOtherFacts`, and must not.
 *
 * ## Two things it deliberately does not do
 *
 * **It has no heading of its own.** A heading would be panel copy describing the write, so the
 * dialog is labelled by the server's message instead (`aria-labelledby`) — which is also the
 * better accessible name, since it is what a screen reader should read first.
 *
 * **A click here is not a stronger confirmation than a host's `confirm: true`.** ADR 0014
 * decision 1 is unchanged by this surface existing: the tier, the stored proposal and the
 * `expectedTool` check are what make the write safe, and this dialog is how a family is asked.
 *
 * ## The one string that was added, and why it is not a third voice (#303)
 *
 * {@link gate} makes the confirm button inert until somebody types a value back — the family's
 * own name, in front of `disband_family`. It carries one sentence of panel copy (*"Type the
 * family's name to confirm"*), and the rule above survives it because **that sentence says
 * nothing about the write**: it is an instruction for a control, of exactly the kind *Cancel*
 * already is. What is being deleted, how much of it, and what deletion cannot reach are still
 * the server's `message` and `doesNotReach`, unedited.
 *
 * The ceremony is here rather than in a dialog of the roster's own because a second dialog
 * would be a second place a write could be described, which is the thing this component
 * exists to prevent. It is **not** a security control and does not pretend to be: #307 is the
 * second factor, enforced server-side on the confirm, and until it lands this is what keeps a
 * mis-click from destroying other members' data.
 */
/**
 * A value that has to be typed back before the confirm is live — see the header.
 *
 * `expected` is compared after trimming and nothing else: a family's name may hold anything,
 * and case-folding *"the riveras"* into a match would make the ceremony weaker than the one
 * drawn without saying so.
 */
export interface ConfirmGate {
  readonly label: string;
  readonly expected: string;
}

export function Confirm({
  message,
  disclosures = [],
  gate,
  pending,
  error,
  onConfirm,
  onCancel,
}: {
  /** The tool's own family-facing sentence. Rendered verbatim, never edited or summarised. */
  message: string;
  /** The tool's own `doesNotReach` lines, if it has any. Server strings, in order. */
  disclosures?: readonly string[];
  gate?: ConfirmGate | undefined;
  pending: boolean;
  /** A generic failure line — never a vendor string or a code (the login app's rule, #57). */
  error: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const labelId = useId();
  const gateId = useId();
  const [typed, setTyped] = useState("");
  const unlocked = gate === undefined || typed.trim() === gate.expected;
  const cancelRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const opener = useRef<HTMLElement | null>(null);

  /**
   * The keyboard contract, and **each half is a separate effect on purpose**.
   *
   * Focus lands on **Cancel** rather than on the confirm — every write this dialog stands in
   * front of is one the family cannot undo, so the key easiest to hit by accident must be the
   * one that does nothing. That happens **once, when the dialog appears**. It used to share an
   * effect with the key handler, whose dependency is `onCancel` — an inline arrow in
   * `Memory.tsx`, and therefore a new identity on every parent render. So pressing *Yes* (which
   * flips `pending`) re-ran the effect and yanked focus back onto the now-disabled Cancel,
   * mid-interaction, immediately after a keyboard user committed to an irreversible write.
   */
  useEffect(() => {
    // Remember what opened this so Cancel can hand focus back. Captured at mount, because by
    // unmount the active element is a button inside this dialog.
    opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    cancelRef.current?.focus();
    return () => {
      // **Only if it is still there.** After an applied write the list is re-read and the row
      // that opened this is a different node, so focusing the old one is impossible and
      // focusing something else would move a keyboard somewhere nobody asked for. On cancel
      // nothing has changed, the control still exists, and this is the case the review
      // separated out: the trigger-is-gone reasoning covers success and not cancellation.
      const previous = opener.current;
      if (previous !== null && document.contains(previous)) previous.focus();
    };
  }, []);

  /**
   * **Escape is ignored once the write is in flight**, which is the half that was wrong.
   *
   * Cancel is disabled while `pending`, but this listener was not — so after *Yes, go ahead* a
   * person could press Escape, watch the dialog vanish, and conclude they had cancelled. The
   * request is not aborted and the delete still commits: a false outcome on the one write with
   * no undo. `pending` now gates the handler, so the dialog stays put until the answer lands.
   */
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || pending) return;
      onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel, pending]);

  /**
   * **Tab is kept inside the dialog**, because `aria-modal="true"` is otherwise a claim nothing
   * makes true.
   *
   * The concrete failure this closes: focus starts on Cancel, which is the *first* control, so
   * **Shift+Tab** — the natural "go back" — left the dialog and landed on the last fact's
   * *Take this back* underneath the scrim, which is enabled while a proposal is merely open.
   * Activating it replaced the pending proposal with a different one, and *Yes* then deleted a
   * fact the family never opened the dialog about.
   *
   * A cycle rather than `inert` on the shell: `inert` needs a handle on a root this component
   * does not own, and the native `<dialog>` top layer would change how the whole panel is
   * mounted. Cycling is the part that fixes the reachable bug, in the component that has the
   * bug. Screens outside the dialog are additionally made unreachable by `Memory.tsx`, which
   * disables every list action while a confirmation is open.
   */
  useEffect(() => {
    const onTab = (event: KeyboardEvent) => {
      if (event.key !== "Tab") return;
      const root = dialogRef.current;
      if (root === null) return;
      // Inputs count: the disband gate puts a text field inside the dialog, and a cycle that
      // only knew about buttons would let Shift+Tab off the first button and out of the modal
      // — the exact escape the comment above is about, one control type wider.
      const focusable = [
        ...root.querySelectorAll<HTMLElement>("button:not([disabled]), input:not([disabled])"),
      ];
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (first === undefined || last === undefined) {
        // Every control is disabled — mid-write. Keep focus in the dialog rather than letting
        // Tab escape to the page behind it.
        event.preventDefault();
        return;
      }
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !root.contains(active))) {
        event.preventDefault();
        last.focus();
        return;
      }
      if (!event.shiftKey && (active === last || !root.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onTab);
    return () => window.removeEventListener("keydown", onTab);
  }, []);

  return (
    <div className="scrim scrim--dialog">
      <div
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelId}
        ref={dialogRef}
      >
        <p className="dialog__message" id={labelId}>
          {message}
        </p>
        {disclosures.length === 0 ? null : (
          <ul className="dialog__list">
            {disclosures.map((line) => (
              <li className="dialog__list-item" key={line}>
                {line}
              </li>
            ))}
          </ul>
        )}
        {gate === undefined ? null : (
          <div className="field dialog__gate">
            <label className="field__label" htmlFor={gateId}>
              {gate.label}
            </label>
            <input
              className="field__input"
              id={gateId}
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              disabled={pending}
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
            />
          </div>
        )}
        {error === null ? null : (
          <p className="dialog__error" role="alert">
            {error}
          </p>
        )}
        <div className="dialog__actions">
          <button
            className="cta cta--quiet"
            type="button"
            onClick={onCancel}
            disabled={pending}
            ref={cancelRef}
          >
            Cancel
          </button>
          <button className="cta" type="button" onClick={onConfirm} disabled={pending || !unlocked}>
            {pending ? "Working…" : "Yes, go ahead"}
          </button>
        </div>
      </div>
    </div>
  );
}
